'use es6';

import PropTypes from 'prop-types';
import { useEffect, useCallback } from 'react';
import I18n from 'I18n';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { isEqual } from 'underscore';
import { connect } from 'react-redux';
import { DragTypes } from 'FormsUI/constants/DragAndDrop';
import { RichTextTypes, SpecialFieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { MoveableArea } from 'foundations-components/transitional/components/MoveableArea';
import { FloatingButtonGroup } from 'foundations-components/transitional/components/FloatingButtonGroup';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Button } from 'foundations-components/transitional/components/Button';
import { startDrag, endDrag } from 'FormsUI/actions/DragState.ts';
import RichText from './previewFields/RichText';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DraggableRichText = props => {
  const {
    formFieldGroupIndex,
    selectedFormItemType,
    selectedFormItemReference,
    richText = {
      content: ''
    },
    handleRichTextSelect,
    handleRichTextDelete,
    handleDrag,
    isQueuedField,
    startDrag: startDragAction,
    endDrag: endDragAction
  } = props;
  const [, dragRef, dragPreviewRef] = useDrag(() => ({
    type: DragTypes.REORDER_RICH_TEXT,
    item: () => {
      setTimeout(() => {
        handleDrag(SpecialFieldTypes.RICH_TEXT);
        startDragAction({
          type: 'richText',
          id: richText.privateInternalId
        });
      });
      const formFieldLabel = richText.type === RichTextTypes.IMAGE ? I18n.text('editor.form.properties.image') : I18n.text('editor.form.properties.richTextLabel');
      return {
        formFieldGroupIndex,
        formFieldName: SpecialFieldTypes.RICH_TEXT,
        formFieldLabel,
        richTextContent: richText.content,
        privateInternalId: richText.privateInternalId,
        isSourceQueued: isQueuedField
      };
    },
    end: () => {
      setTimeout(() => {
        handleDrag();
        endDragAction();
      });
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  }), [formFieldGroupIndex, richText, isQueuedField, handleDrag]);

  // Hide default drag preview
  useEffect(() => {
    dragPreviewRef(getEmptyImage());
  }, [dragPreviewRef]);
  const isSelected = useCallback(() => {
    return (isEqual(SpecialFieldTypes.RICH_TEXT, selectedFormItemType) || isEqual(SpecialFieldTypes.IMAGE, selectedFormItemType)) && isEqual(richText.privateInternalId, selectedFormItemReference);
  }, [selectedFormItemType, selectedFormItemReference, richText.privateInternalId]);
  const handleClick = useCallback(({
    target: {
      value
    }
  }) => {
    handleRichTextSelect(value, richText.privateInternalId, selectedFormItemType);
  }, [handleRichTextSelect, richText.privateInternalId, selectedFormItemType]);
  const handleEdit = useCallback(() => {
    handleRichTextSelect(!isSelected(), richText.privateInternalId, selectedFormItemType);
  }, [handleRichTextSelect, richText.privateInternalId, selectedFormItemType, isSelected]);
  const handleDelete = useCallback(() => {
    handleRichTextDelete(formFieldGroupIndex);
    handleRichTextSelect(false, richText.privateInternalId);
  }, [handleRichTextDelete, formFieldGroupIndex, handleRichTextSelect, richText.privateInternalId]);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "draggable-rich-text",
    children: /*#__PURE__*/_jsx(FloatingButtonGroup, {
      buttonGroup: /*#__PURE__*/_jsxs(ButtonGroup, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: handleEdit,
          children: /*#__PURE__*/_jsx(EditIcon, {})
        }), /*#__PURE__*/_jsx(Button, {
          onClick: handleDelete,
          children: /*#__PURE__*/_jsx(DeleteIcon, {})
        })]
      }),
      children: /*#__PURE__*/_jsx(MoveableArea, {
        "data-test-id": "draggable-form-element",
        containerDraggable: true,
        connectDragSource: dragRef,
        selected: isSelected(),
        onSelectedChange: handleClick,
        children: /*#__PURE__*/_jsx(RichText, {
          type: richText.type,
          content: richText.content
        })
      })
    })
  });
};
DraggableRichText.propTypes = {
  formFieldGroupIndex: PropTypes.number.isRequired,
  selectedFormItemType: PropTypes.string,
  selectedFormItemReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  richText: PropTypes.object,
  handleRichTextSelect: PropTypes.func.isRequired,
  handleRichTextDelete: PropTypes.func.isRequired,
  handleDrag: PropTypes.func.isRequired,
  isQueuedField: PropTypes.bool,
  // Redux props
  startDrag: PropTypes.func.isRequired,
  endDrag: PropTypes.func.isRequired
};
const mapDispatchToProps = {
  startDrag,
  endDrag
};
export default connect(null, mapDispatchToProps)(DraggableRichText);